import { useEffect } from 'react';
import { useBrandSettings } from '../hooks/useBrandSettings';

export function BrandThemeProvider({ children }: { children: React.ReactNode }) {
  const { settings } = useBrandSettings();

  useEffect(() => {
    // Apply brand colors as CSS custom properties
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', settings.primaryColor);
    root.style.setProperty('--brand-secondary', settings.secondaryColor);
    root.style.setProperty('--brand-accent', settings.accentColor);
    root.style.setProperty('--brand-background', settings.backgroundColor);
    root.style.setProperty('--brand-container', settings.containerColor);
    root.style.setProperty('--brand-text', settings.textColor);
    root.style.setProperty('--brand-card-background', settings.cardBackgroundColor);

    // Create dynamic CSS for brand-specific styling
    const styleId = 'brand-theme-styles';
    let existingStyle = document.getElementById(styleId);
    
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      /* Global page background */
      body {
        background-color: ${settings.backgroundColor} !important;
        color: ${settings.textColor} !important;
      }
      
      /* Main container backgrounds */
      .bg-gray-50 {
        background-color: ${settings.containerColor} !important;
      }
      
      /* Card backgrounds */
      .bg-white {
        background-color: ${settings.cardBackgroundColor} !important;
      }
      
      /* Brand color classes */
      .brand-primary {
        color: ${settings.primaryColor} !important;
      }
      .brand-primary-bg {
        background-color: ${settings.primaryColor} !important;
      }
      .brand-primary-border {
        border-color: ${settings.primaryColor} !important;
      }
      .brand-secondary {
        color: ${settings.secondaryColor} !important;
      }
      .brand-secondary-bg {
        background-color: ${settings.secondaryColor} !important;
      }
      .brand-accent {
        background-color: ${settings.accentColor} !important;
      }
      .brand-background {
        background-color: ${settings.backgroundColor} !important;
      }
      .brand-container {
        background-color: ${settings.containerColor} !important;
      }
      .brand-card-background {
        background-color: ${settings.cardBackgroundColor} !important;
      }
      .brand-text {
        color: ${settings.textColor} !important;
      }
      
      /* Interactive elements */
      .brand-button-primary {
        background-color: ${settings.primaryColor} !important;
        border-color: ${settings.primaryColor} !important;
        color: #FFFFFF !important;
      }
      .brand-button-primary:hover {
        background-color: ${adjustColor(settings.primaryColor, -10)} !important;
      }
      .brand-tab-active {
        color: ${settings.primaryColor} !important;
        border-bottom-color: ${settings.primaryColor} !important;
      }
      
      /* Header styling */
      .bg-background {
        background-color: ${settings.cardBackgroundColor} !important;
      }
      
      /* Border colors */
      .border-gray-200 {
        border-color: ${adjustColor(settings.containerColor, 20)} !important;
      }
      
      /* Text colors */
      .text-gray-900 {
        color: ${settings.textColor} !important;
      }
      .text-gray-600 {
        color: ${adjustColor(settings.textColor, 50)} !important;
      }
      .text-gray-500 {
        color: ${adjustColor(settings.textColor, 100)} !important;
      }
      .text-muted-foreground {
        color: ${adjustColor(settings.textColor, 100)} !important;
      }
    `;
    
    document.head.appendChild(style);

    return () => {
      // Cleanup on unmount
      if (existingStyle) {
        existingStyle.remove();
      }
    };
  }, [settings]);

  return <>{children}</>;
}

// Helper function to adjust color brightness
function adjustColor(color: string, amount: number): string {
  const usePound = color[0] === '#';
  const col = usePound ? color.slice(1) : color;
  
  const num = parseInt(col, 16);
  let r = ((num >> 16) & 0xFF) + amount;
  let g = ((num >> 8) & 0x00FF) + amount;
  let b = (num & 0x0000FF) + amount;
  
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  
  return (usePound ? '#' : '') + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
}
